// "Getting started in 60 seconds" — an animated, auto-advancing walkthrough
// shown at the top of the public Help Center. Behaves like a lightweight video
// (play/pause + step dots) but is pure React/CSS: no file to host, loads
// instantly, and stays in sync with the product because it's just code.
import { useState, useEffect, Link, motion, AnimatePresence, Button, Card, Sparkles, Share, CalendarDays, TrendingUp, Users, Check, ArrowRight } from "@/pages/shared";
import { Play, Pause } from "lucide-react";
import { CLAIMS } from "@/config/claims";

const STEPS = [
  {
    title: "Create your free account",
    icon: Users,
    color: "text-violet-500",
    bullets: ["Sign up with email or Google in seconds", `Start with ${CLAIMS.freeAllowance.generations} free AI generations per ${CLAIMS.freeAllowance.period}`, "Set your Brand Kit so everything comes out on-brand"],
    visual: (
      <div className="space-y-2">
        <div className="h-8 bg-white/10 rounded flex items-center px-3 text-xs text-muted-foreground">you@business.com</div>
        <div className="h-9 bg-violet-600 rounded-full flex items-center justify-center text-sm font-medium text-white">Sign Up Free</div>
        <p className="text-[10px] text-center text-muted-foreground">✓ {CLAIMS.freeAllowance.generations} free generations / {CLAIMS.freeAllowance.period}</p>
      </div>
    ),
  },
  {
    title: "Generate content with AI",
    icon: Sparkles,
    color: "text-blue-500",
    bullets: ["Pick a type: blog, social, ad copy, thread, caption or script", "Type your topic — AI writes it in seconds", "Add an AI image or a Sora 2 video clip in one click"],
    visual: (
      <div className="space-y-2">
        <div className="h-8 bg-white/10 rounded flex items-center px-3 text-xs">bridal makeup tips for summer</div>
        <div className="flex gap-1.5">
          <span className="px-2 py-1 bg-blue-500/30 border border-blue-500 rounded text-[10px]">Social</span>
          <span className="px-2 py-1 bg-white/10 rounded text-[10px]">Blog</span>
          <span className="px-2 py-1 bg-white/10 rounded text-[10px]">TikTok</span>
        </div>
        <div className="h-9 bg-gradient-to-r from-blue-600 to-blue-500 rounded-full flex items-center justify-center text-sm font-medium text-white">✨ Generate</div>
      </div>
    ),
  },
  {
    title: "Connect your socials",
    icon: Share,
    color: "text-teal-500",
    bullets: ["Settings → Connect your LinkedIn & X (30 seconds each)", "Posts then publish as YOU, to YOUR followers", "We email you before a connection ever expires"],
    visual: (
      <div className="space-y-2">
        {["LinkedIn", "X (Twitter)"].map((p) => (
          <div key={p} className="flex items-center justify-between bg-white/10 rounded-lg px-3 py-2">
            <span className="text-xs font-medium">{p}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-teal-500/20 text-teal-400">✓ Connected</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    title: "Schedule & auto-publish",
    icon: CalendarDays,
    color: "text-amber-500",
    bullets: ["Pick a date & time — shown in your own timezone", "Tick X and/or LinkedIn — Bluesky & Telegram cross-post too", "Auto-publishes at the scheduled time (Facebook & Instagram: one-click share, pending Meta review)"],
    visual: (
      <div className="space-y-2">
        <div className="h-8 bg-white/10 rounded flex items-center px-3 text-xs text-muted-foreground">Fri 20 Jun · 09:00</div>
        <div className="flex gap-1.5 flex-wrap">
          {["X ✓", "LinkedIn ✓", "Bluesky", "Telegram"].map((p, i) => (
            <span key={p} className={`px-2 py-1 rounded text-[10px] ${i < 2 ? "bg-teal-500/20 text-teal-400 border border-teal-500/40" : "bg-white/10 text-muted-foreground"}`}>{p}</span>
          ))}
        </div>
        <div className="h-9 bg-teal-600 rounded-full flex items-center justify-center text-sm font-medium text-white">Schedule — posts itself</div>
      </div>
    ),
  },
  {
    title: "Monetize your audience",
    icon: TrendingUp,
    color: "text-purple-500",
    bullets: ["Affiliate links with click tracking & auto-UTMs", "Lead capture forms with instant email alerts", "PDF flyers + referral credits for inviting friends"],
    visual: (
      <div className="space-y-2">
        <div className="flex justify-between text-xs"><span>Link clicks</span><span className="font-semibold text-purple-400">1,247</span></div>
        <div className="h-2 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-purple-500 rounded-full" style={{ width: "75%" }} /></div>
        <div className="flex justify-between text-xs"><span>Leads captured</span><span className="font-semibold text-teal-400">89</span></div>
        <div className="h-2 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-teal-500 rounded-full" style={{ width: "45%" }} /></div>
      </div>
    ),
  },
];

const STEP_DURATION_MS = 6000;

export const GettingStartedWalkthrough = () => {
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    if (!playing) return;
    const t = setInterval(() => setStep((s) => (s + 1) % STEPS.length), STEP_DURATION_MS);
    return () => clearInterval(t);
  }, [playing]);

  const current = STEPS[step];
  const Icon = current.icon;

  return (
    <Card className="glass overflow-hidden mb-10 border-violet-500/20" data-testid="getting-started-walkthrough">
      <div className="p-4 sm:p-6">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-violet-500/20 flex items-center justify-center"><Play className="w-3.5 h-3.5 text-violet-400" /></span>
              Getting started in 60 seconds
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">From blank page to auto-published, monetized content — in 5 steps.</p>
          </div>
          <button
            onClick={() => setPlaying(!playing)}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            data-testid="walkthrough-play-pause"
          >
            {playing ? <><Pause className="w-3 h-3" />Pause</> : <><Play className="w-3 h-3" />Play</>}
          </button>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.35 }}
            className="grid md:grid-cols-2 gap-5 items-center min-h-[170px]"
            data-testid={`walkthrough-step-${step + 1}`}
          >
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[10px] font-bold tracking-widest text-muted-foreground">STEP {step + 1} OF {STEPS.length}</span>
              </div>
              <h3 className={`text-xl font-semibold mb-3 flex items-center gap-2`}><Icon className={`w-5 h-5 ${current.color}`} />{current.title}</h3>
              <div className="space-y-1.5 text-sm text-muted-foreground">
                {current.bullets.map((b) => (
                  <p key={b} className="flex items-start gap-2"><Check className="w-4 h-4 text-teal-500 mt-0.5 shrink-0" />{b}</p>
                ))}
              </div>
            </div>
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">{current.visual}</div>
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-5 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            {STEPS.map((s, i) => (
              <button
                key={s.title}
                onClick={() => { setStep(i); setPlaying(false); }}
                className="group relative h-1.5 rounded-full overflow-hidden bg-white/15 transition-all"
                style={{ width: i === step ? 44 : 20 }}
                aria-label={`Step ${i + 1}: ${s.title}`}
                data-testid={`walkthrough-dot-${i + 1}`}
              >
                {i === step && playing && (
                  <span
                    key={`bar-${step}`}
                    className="absolute inset-y-0 left-0 bg-violet-500 rounded-full"
                    style={{ animation: `walkthroughProgress ${STEP_DURATION_MS}ms linear forwards` }}
                  />
                )}
                {i === step && !playing && <span className="absolute inset-0 bg-violet-500 rounded-full" />}
                {i < step && <span className="absolute inset-0 bg-violet-500/50 rounded-full" />}
              </button>
            ))}
          </div>
          <Link to="/register">
            <Button size="sm" className="btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full text-xs" data-testid="walkthrough-cta">
              Try it yourself — free <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
      <style>{`@keyframes walkthroughProgress { from { width: 0%; } to { width: 100%; } }`}</style>
    </Card>
  );
};
